import { queryKeys } from "@healthos/client-data";
import type { BriefCopy } from "@healthos/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { client, jsonOrNull, jsonOrThrow } from "../../../api/healthos";
import { companyScopedQuery, useOpsCompany } from "../../../components/ops/ops-company-context";
import {
  cohortConfigQueryString,
  DEFAULT_COHORT_CONFIG,
  type DemoCohortConfig,
  readCohortConfig,
  subscribeCohortConfig,
} from "../../../lib/demo-cohort-config";
import type { DashboardPayload, OperationsMe, SavedScenario } from "./_shared";

export type OperationsBriefResponse = BriefCopy & {
  source?: "llm" | "template";
  rationale?: string;
};

import { hasCap } from "./_shared";

/**
 * Shared TanStack Query hooks for the operations dashboard. Children
 * call these directly (not via outlet context) so each route module
 * can be unit-tested in isolation; TanStack's queryKey-level dedup
 * means the layout + child rendering at the same time only fires one
 * network request per key.
 */

/**
 * Subscribe to the operator's demo cohort overrides (headcount + PMPM)
 * so changes via the DemoPersonaSwitcher refetch the dashboard. The
 * config sits in localStorage; this hook returns a live value and the
 * URL/queryKey fragment that scopes the React Query cache to it.
 */
// Demo overrides are a dev/demo affordance, never part of the customer
// read path: production builds always query the canonical cohort.
const DEMO_OVERRIDES_ENABLED = import.meta.env.DEV || import.meta.env.VITE_DEMO_TOOLS === "true";

function useCohortConfig(): DemoCohortConfig {
  const [cfg, setCfg] = useState<DemoCohortConfig>(() =>
    DEMO_OVERRIDES_ENABLED ? readCohortConfig() : DEFAULT_COHORT_CONFIG,
  );
  useEffect(() => {
    if (!DEMO_OVERRIDES_ENABLED) return;
    return subscribeCohortConfig(setCfg);
  }, []);
  return cfg;
}

export function useDashboard() {
  const cohort = useCohortConfig();
  const { companyId } = useOpsCompany();
  const queryString = cohortConfigQueryString(cohort);
  return useQuery({
    // Cohort config + company scope are part of the cache key so changing
    // headcount, PMPM, or the platform company switcher forces a fresh
    // fetch (and doesn't pollute the no-override canonical cache).
    queryKey: ["operations", "dashboard", queryString, companyId],
    queryFn: async () => {
      const params = new URLSearchParams(queryString);
      const query = companyScopedQuery(companyId, Object.fromEntries(params.entries()));
      return jsonOrThrow<DashboardPayload>(
        await client().api.operations.dashboard.$get({ query }),
        "GET /api/operations/dashboard",
      );
    },
  });
}

export function useOperationsBrief() {
  const cohort = useCohortConfig();
  const { companyId } = useOpsCompany();
  const queryString = cohortConfigQueryString(cohort);
  return useQuery({
    queryKey: ["operations", "brief", queryString, companyId],
    queryFn: async () => {
      const params = new URLSearchParams(queryString);
      const query = companyScopedQuery(companyId, Object.fromEntries(params.entries()));
      const brief = await jsonOrNull<OperationsBriefResponse>(
        await client().api.operations.brief.$get({ query }),
        "GET /api/operations/brief",
      );
      // Keep audioUrl app-relative: playback resolves it through the
      // authed fetch (getAuthedAudioObjectUrl), not a raw <audio src>.
      // An empty audioUrl means no narration; drop it so no <audio>
      // element renders at all.
      if (brief && !brief.audioUrl) {
        const { audioUrl: _empty, ...rest } = brief;
        return rest as OperationsBriefResponse;
      }
      return brief;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useOperationsMe({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["operations", "me"],
    enabled,
    queryFn: () =>
      client()
        .api.operations.me.$get()
        .then((r) => {
          // 403 means "no ops seat": that is a definitive denial, not an
          // error to swallow. Network/server failures throw so the shell
          // denies rather than degrades.
          if (r.status === 403) return null;
          return jsonOrThrow<OperationsMe>(r, "GET /api/operations/me");
        }),
  });
}

export function useScenarios(me: OperationsMe | null) {
  const { companyId } = useOpsCompany();
  return useQuery({
    queryKey: [...queryKeys.operationsScenarios, companyId],
    enabled: hasCap("ops:list_scenarios", me),
    queryFn: () =>
      client()
        .api.operations.scenarios.$get({ query: companyScopedQuery(companyId) })
        .then((r) =>
          jsonOrNull<{ scenarios?: SavedScenario[] }>(r, "GET /api/operations/scenarios"),
        )
        .catch(() => null),
  });
}

export function useRefreshScenarios() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.operationsScenarios });
  };
}
